import { IEntitySwitcherItem } from '@opengov/components-navigation';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import Fuse from 'fuse.js';

import { entitySelector } from './actionsAndReducer';
import { ENTITY_SEARCH_FIELDS, EntitySwitcherItem } from './records';

export const getEntity = createSelector(entitySelector, (state) => state.entity);
export const getRequestedEntity = createSelector(entitySelector, (state) => state.requestedEntity);
export const getEntityId = createSelector(getEntity, (entity) => entity.id);
export const getNumericalEntityId = createSelector(getEntity, (entity) => entity.entity_id);
export const getEntitySubdomain = createSelector(getEntity, (entity) => entity.subdomain);
export const getEntityFiscalYearStartMonth = createSelector(getEntity, (entity) => entity.fiscal_year_start_month);
export const getEntityName = createSelector(getEntity, (entity) => entity.name);
export const selectEntityLogo = createSelector(getEntity, (entity) => entity.logo);
export const selectEntityHomepageUrl = createSelector(getEntity, (entity) => entity.homepage_url);
export const entityFeaturePermissionsSelector = createSelector(getEntity, (entity) => entity.feature_permissions);
export const isEntitySelectedSelector = createSelector(getEntity, (entity) => entity.id !== 'controlpanel');
export const selectEntityDisplayName = createSelector(getEntity, (entity) => entity.name);
export const getEntities = createSelector(entitySelector, (state) => state.entities);
export const getEntitySearchText = createSelector(entitySelector, (state) => state.searchText);
export const isEntitySwitcherOpen = createSelector(entitySelector, (state) => state.isSelectPanelOpen);
export const getEntityUuid = createSelector(getEntity, (entity) => entity.uuid);
export const getSearchableEntities = createSelector(getEntities, (entities) => {
  return entities.map((entity) =>
    entity.withMutations((mutableEntity) => {
      ENTITY_SEARCH_FIELDS.forEach((key) => {
        mutableEntity.update(key, (value) => (!!value ? value.toString() : ''));
      });
    })
  );
});

export const getEntitiesFuseInstance = createSelector(getSearchableEntities, (entities) => {
  return new Fuse(entities.toArray(), {
    keys: ENTITY_SEARCH_FIELDS,
    threshold: 0.2
  });
});

export const getSelectableEntities = createSelector(
  getSearchableEntities,
  getEntitiesFuseInstance,
  getEntitySearchText,
  (entities, fuse, searchText): IEntitySwitcherItem[] => {
    if (searchText === '') {
      return entities.map(EntitySwitcherItem).toArray();
    }
    return fuse.search(searchText);
  }
);

export interface ICurrentEntity {
  id: string;
  subdomain: string;
  entity_id: number;
  default_coa_id: string;
  uuid?: string;
  feature_permissions?: string[];
  fiscal_year_start_month: number;
}

export const selectCurrentEntity = createStructuredSelector<any, ICurrentEntity>({
  id: getEntityId,
  subdomain: getEntitySubdomain,
  entity_id: createSelector(getEntity, (e) => e.entity_id),
  default_coa_id: createSelector(getEntity, (e) => e.default_coa_id),
  uuid: createSelector(getEntity, (e) => e.uuid),
  fiscal_year_start_month: createSelector(getEntity, (e) => e.fiscal_year_start_month ?? 1)
});
